<template>
  <div class="slider-container def-slider">
    <label
      :for="inputId"
      class="slider-label"
    >
      {{ label }}<span v-if="required" aria-hidden="true"> *</span>
    </label>
    <input
      :id="inputId"
      type="range"
      :value="localValue"
      :min="min"
      :max="max"
      :step="step"
      class="slider"
      v-bind="inputAttrs"
      @input="onRangeInput"
      :aria-valuetext="ariaValueText"
      :aria-invalid="inputError ? 'true' : null"
      :aria-describedby="describedBy"
      :aria-required="required ? 'true' : null"
    />
    <div class="slider-value">
      <span>{{ formattedValue }}</span>
    </div>
    <div v-if="inputError" :id="errorId" class="slider-error" role="alert" aria-live="assertive">
      {{ inputError }}
    </div>
  </div>
</template>

<script>
import Utils from "@/utils/utils";
export default {
  name: 'Slider',
  inheritAttrs: false,
  props: {
    value: [String, Number],
    label: { type: String, required: true },
    min: { type: [String, Number], default: '' },
    max: { type: [String, Number], default: '' },
    step: { type: [String, Number], default: 1 },
    currency: { type: String, default: null },
    currencyDisplay: { type: String, default: 'symbol' },
    allowedValues: { type: Array, default: () => [] },
    required: { type: Boolean, default: false },
    id: { type: String, default: null },
  },
  data() {
    return {
      localValue: this.normalizeValue(this.value),
      inputError: '',
      config: window.config,
    };
  },
  watch: {
    value(newVal) {
      this.localValue = this.normalizeValue(newVal);
    }
  },
  computed: {
    inputId() {
      return this.id || `slider-${this._uid}`;
    },
    errorId() {
      return `${this.inputId}-error`;
    },
    describedBy() {
      const ids = [];
      if (this.$attrs['aria-describedby']) {
        ids.push(this.$attrs['aria-describedby']);
      }
      if (this.inputError) {
        ids.push(this.errorId);
      }
      return ids.join(' ') || null;
    },
    inputAttrs() {
      const attrs = { ...this.$attrs };
      delete attrs['aria-label'];
      delete attrs['aria-describedby'];
      delete attrs['aria-invalid'];
      delete attrs['aria-valuetext'];
      return attrs;
    },
    formattedValue() {
      if (!this.currency || this.currencyDisplay === 'hidden' || this.localValue === '' || this.localValue === null || isNaN(this.localValue)) {
        return this.localValue;
      }
      return Utils.formatCurrency(
        Number(this.localValue),
        this.currency || this.config.ACCOUNT_BALANCE_CURRENCY,
        true,
        this.config.ACCOUNT_BALANCE_SEPARATOR
      );
    },
    ariaValueText() {
      if (!this.currency || this.currencyDisplay === 'hidden') {
        return null;
      }

      if (this.formattedValue === '' || this.formattedValue === null || this.formattedValue === undefined) {
        return null;
      }
      return String(this.formattedValue);
    }
  },
  methods: {
    normalizeValue(value) {
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }

      if (this.allowedValues.length > 0) {
        return this.allowedValues[0];
      }

      return this.min;
    },
    onRangeInput(event) {
      const value = event.target.value;
      const num = value === '' ? '' : parseFloat(value);

      if (this.allowedValues.length > 0 && !this.allowedValues.includes(num)) {
        this.inputError = `Allowed values: ${this.allowedValues.join(', ')}`;
        event.target.value = this.localValue;
        return;
      }

      this.inputError = '';
      this.localValue = num;
      this.$emit('input', num);
    }
  },
};
</script>

<style scoped>
.slider-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.slider-label {
  margin-bottom: 0.5rem;
}

.slider-value {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.5em;
}

.slider {
  width: 100%;
}

.slider-error {
  margin-top: 0.25rem;
  text-align: left;
}
</style>
